import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useImageGeneration from '../hooks/useImageGeneration';
import { useWordTracker } from './WordTrackerContext';

// Create the context
const GameContext = createContext();

// Custom hook to use the game context
export const useGame = () => useContext(GameContext);

// Provider component
export const GameProvider = ({ children }) => {
  const navigate = useNavigate();
  const { image, revisedPrompt, loading, error, generateNewImage, resetImage } = useImageGeneration();
  const { markWordAsUsed } = useWordTracker();
  
  // Use React state for all game state
  const [gameActive, setGameActive] = useState(false);
  const [category, setCategory] = useState('');
  const [currentWord, setCurrentWord] = useState('');
  const [guessesRemaining, setGuessesRemaining] = useState(6);
  const [sessionLives, setSessionLives] = useState(3);
  const [blurLevel, setBlurLevel] = useState(30);
  const [score, setScore] = useState(0);
  const [gameHistory, setGameHistory] = useState([]);
  const [gameResult, setGameResult] = useState({ win: false, word: '', image: '' });
  
  // Reset the game state
  const resetGame = useCallback(() => {
    setCurrentWord('');
    setGameActive(false);
    setCategory('');
    setGuessesRemaining(6);
    setBlurLevel(30);
    setGameResult({ win: false, word: '', image: '' });
    resetImage();
  }, [resetImage]);
  
  // Start a new game
  const startGame = useCallback(async (selectedCategory, wordToGuess) => {
    resetGame();
    
    const normalizedWord = wordToGuess.toLowerCase().trim();
    setCurrentWord(normalizedWord);
    setCategory(selectedCategory);
    setGameActive(true);
    
    try {
      // Generate an image for the word
      await generateNewImage(wordToGuess, selectedCategory);
      
      // Navigate to the game screen
      navigate('/game');
    } catch (err) {
      console.error('Error starting game:', err);
    }
  }, [resetGame, generateNewImage, navigate]);
  
  // Process a guess
  const processGuess = useCallback((guess) => {
    // Skip if game is not active
    if (!gameActive) return false;
    
    const normalizedGuess = guess.toLowerCase().trim();
    const normalizedWord = currentWord.toLowerCase().trim();
    
    // Check if the guess is correct
    const isCorrect = normalizedGuess === normalizedWord;
    
    if (isCorrect) {
      // Player won
      const newScore = score + guessesRemaining * 100;
      setScore(newScore);
      setGameResult({ 
        win: true, 
        word: normalizedWord, 
        image 
      });
      setGameActive(false);
      
      // Mark the word as used in the word tracker
      if (category !== 'custom') {
        markWordAsUsed(category, normalizedWord);
      }
      
      // Add to history
      const historyEntry = { 
        word: normalizedWord, 
        won: true, 
        guessesLeft: guessesRemaining 
      };
      setGameHistory(prev => [...prev, historyEntry]);
      
      // Navigate to results
      setTimeout(() => navigate('/results'), 1500);
      
      return true;
    }
    
    // Incorrect guess
    const newGuesses = guessesRemaining - 1;
    setGuessesRemaining(newGuesses);
    
    // Reduce blur level with each incorrect guess
    const blurStep = 30 / 6; // Initial blur divided by max guesses
    const newBlurLevel = Math.max(0, blurLevel - blurStep);
    setBlurLevel(newBlurLevel);
    
    // Check if player lost
    if (newGuesses <= 0) {
      // Player lost
      setGameResult({ 
        win: false, 
        word: normalizedWord, 
        image 
      });
      setGameActive(false);
      
      // Lose a session life
      const newLives = sessionLives - 1;
      setSessionLives(newLives);
      
      // Add to history
      const historyEntry = { 
        word: normalizedWord, 
        won: false, 
        guessesLeft: 0 
      };
      setGameHistory(prev => [...prev, historyEntry]);
      
      // Check if all session lives are lost
      if (newLives <= 0) {
        // Reset session lives but keep the score
        setSessionLives(3);
        // Navigate to game over screen
        setTimeout(() => navigate('/game-over'), 1500);
      } else {
        // Navigate to results
        setTimeout(() => navigate('/results'), 1500);
      }
    }
    
    return false;
  }, [image, navigate, markWordAsUsed, guessesRemaining, sessionLives, blurLevel, score, category, gameActive, currentWord]);
  
  // Value object to provide to consumers
  const value = {
    // State
    gameActive,
    category,
    currentWord,
    guessesRemaining,
    sessionLives,
    blurLevel,
    score,
    gameHistory,
    gameResult,
    image,
    revisedPrompt,
    loading,
    error,
    
    // Actions
    startGame,
    processGuess,
    resetGame,
    resetSessionLives: () => setSessionLives(3),
    generateNewImage,
    setScore
  };
  
  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export default GameContext;